import { LegalPageLayout } from "@/components/legal/LegalPageLayout";
import { LegalSection } from "@/components/legal/LegalSection";

const tableOfContents = [
  { id: "introduction", title: "Introduction", level: 1 },
  { id: "community-standards", title: "Community Standards", level: 1 },
  { id: "prohibited-content", title: "Prohibited Content", level: 1 },
  { id: "hate-speech", title: "Hate Speech Policy", level: 2 },
  { id: "harassment", title: "Harassment and Bullying", level: 2 },
  { id: "sexual-content", title: "Sexual Content and Minors", level: 2 },
  { id: "illegal-activity", title: "Illegal Activity", level: 2 },
  { id: "spam-policy", title: "Spam and Inauthentic Behavior", level: 2 },
  { id: "enforcement", title: "Enforcement and Consequences", level: 1 },
  { id: "reporting", title: "Reporting Violations", level: 1 },
  { id: "age-rating", title: "Age Rating and Restrictions", level: 1 },
  { id: "law-enforcement", title: "Law Enforcement Cooperation", level: 1 },
  { id: "updates", title: "Updates to Guidelines", level: 1 },
  { id: "contact", title: "Contact Information", level: 1 },
];

export default function CommunityGuidelinesPage() {
  return (
    <LegalPageLayout
      title="Community Guidelines & EULA"
      lastUpdated="January 8, 2025"
      language="en"
      alternateUrl="/vi/community-guidelines"
      tableOfContents={tableOfContents}
    >
      <LegalSection id="introduction" title="Introduction">
        <p>
          Welcome to Locuno, a social networking platform that connects people
          within local communities. These Community Guidelines and End User
          License Agreement (EULA) establish the rules and standards that govern
          your use of our platform.
        </p>
        <p>
          By using Locuno, you agree to abide by these guidelines and understand
          that violations may result in immediate content removal and account
          termination. These guidelines apply to all users, content, and
          interactions on our platform.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
          <p className="text-amber-800 font-medium">
            <strong>Age Restriction:</strong> Locuno has a 17+ age rating. Users
            must be at least 17 years old to create an account and use our
            services.
          </p>
        </div>
      </LegalSection>

      <LegalSection id="community-standards" title="Community Standards">
        <p>
          Locuno is committed to fostering a safe, respectful, and inclusive
          environment for all community members. Our platform is designed to
          connect neighbors and build stronger local communities through
          positive interactions.
        </p>
        <p>We expect all users to:</p>
        <ul>
          <li>Treat others with respect and kindness</li>
          <li>Share authentic and truthful information</li>
          <li>Respect privacy and personal boundaries</li>
          <li>Follow local laws and regulations</li>
          <li>Contribute positively to community discussions</li>
          <li>Report inappropriate content when encountered</li>
        </ul>
      </LegalSection>

      <LegalSection id="prohibited-content" title="Prohibited Content">
        <p>
          The following types of content and behavior are strictly prohibited on
          Locuno and will result in immediate action against your account:
        </p>
      </LegalSection>

      <LegalSection id="hate-speech" title="Hate Speech Policy" level={2}>
        <p>
          <strong>Zero tolerance policy:</strong> Locuno has absolutely no
          tolerance for hate speech of any kind. This includes but is not
          limited to:
        </p>
        <ul>
          <li>
            Content that attacks individuals or groups based on race, ethnicity,
            religion, gender, sexual orientation, disability, or nationality
          </li>
          <li>Discriminatory language, slurs, or derogatory terms</li>
          <li>Content promoting supremacist ideologies or extremist views</li>
          <li>Symbols, imagery, or references associated with hate groups</li>
        </ul>
        <p>
          <strong>Consequence:</strong> Immediate and permanent account
          termination. No warnings will be issued for hate speech violations.
        </p>
      </LegalSection>

      <LegalSection id="harassment" title="Harassment and Bullying" level={2}>
        <p>
          Harassment, bullying, and intimidation have no place in our community.
          Prohibited behaviors include:
        </p>
        <ul>
          <li>Targeted harassment of individuals or groups</li>
          <li>Cyberbullying, including persistent unwanted contact</li>
          <li>Doxxing or sharing personal information without consent</li>
          <li>Threats of violence or harm</li>
          <li>Coordinated harassment campaigns</li>
          <li>Stalking or persistent unwanted attention</li>
        </ul>
        <p>
          <strong>Consequence:</strong> Immediate account suspension or
          termination depending on severity.
        </p>
      </LegalSection>

      <LegalSection
        id="sexual-content"
        title="Sexual Content and Minors"
        level={2}
      >
        <p>
          <strong>Absolute prohibition of all sexual content:</strong> Locuno
          completely prohibits any sexual content, including but not limited to
          explicit images, videos, text, or descriptions of a sexual nature. We
          maintain a clean, community-focused platform suitable for families.
        </p>
        <p>
          <strong>Particularly serious:</strong> Any sexual content involving
          minors is strictly forbidden and will be immediately reported to law
          enforcement. This includes:
        </p>
        <ul>
          <li>Any sexualized content featuring individuals under 18</li>
          <li>Grooming behavior or inappropriate contact with minors</li>
          <li>Sharing, requesting, or discussing illegal sexual material</li>
          <li>Content that sexualizes or exploits children in any way</li>
        </ul>
        <p>
          <strong>General sexual content:</strong> In addition to the strict
          rules regarding minors, all explicit sexual content involving adults
          is also prohibited on our platform. Locuno is designed as a safe
          community space appropriate for ages 17+.
        </p>
        <p>
          <strong>Consequence:</strong> Immediate permanent account termination
          and reporting to appropriate authorities.
        </p>
      </LegalSection>

      <LegalSection id="illegal-activity" title="Illegal Activity" level={2}>
        <p>
          Locuno prohibits all illegal activities and content that promotes or
          facilitates illegal behavior, including:
        </p>
        <ul>
          <li>Drug trafficking, sales, or distribution</li>
          <li>Fraud, scams, or financial crimes</li>
          <li>Theft, robbery, or property crimes</li>
          <li>Violence or threats of violence</li>
          <li>Copyright infringement or intellectual property theft</li>
          <li>Money laundering or other financial crimes</li>
          <li>
            Any activity that violates local, national, or international law
          </li>
        </ul>
        <p>
          <strong>Consequence:</strong> Immediate account termination and
          cooperation with law enforcement as required.
        </p>
      </LegalSection>

      <LegalSection
        id="spam-policy"
        title="Spam and Inauthentic Behavior"
        level={2}
      >
        <p>
          To maintain the quality and authenticity of our community, the
          following are prohibited:
        </p>
        <ul>
          <li>Spam messages, posts, or repetitive content</li>
          <li>Fake accounts or impersonation</li>
          <li>Automated posting or bot activity</li>
          <li>Misleading or false information</li>
          <li>Excessive self-promotion or commercial spam</li>
          <li>Manipulation of platform features or engagement</li>
        </ul>
        <p>
          <strong>Consequence:</strong> Content removal, account suspension, or
          termination based on severity and frequency.
        </p>
      </LegalSection>

      <LegalSection id="enforcement" title="Enforcement and Consequences">
        <p>
          Locuno takes violations of these guidelines seriously and employs both
          automated systems and human review to detect and address inappropriate
          content and behavior.
        </p>

        <h3>Enforcement Actions</h3>
        <ul>
          <li>
            <strong>Content Removal:</strong> Immediate removal of violating
            posts, comments, or media
          </li>
          <li>
            <strong>Account Warnings:</strong> Formal warnings for minor
            violations
          </li>
          <li>
            <strong>Temporary Suspension:</strong> Temporary restriction of
            account access
          </li>
          <li>
            <strong>Permanent Termination:</strong> Permanent removal of account
            and all associated data
          </li>
        </ul>

        <h3>No Appeal Policy</h3>
        <p>
          For serious violations including hate speech, harassment, sexual
          content involving minors, and illegal activity, account terminations
          are final and not subject to appeal.
        </p>
      </LegalSection>

      <LegalSection id="reporting" title="Reporting Violations">
        <p>
          Community members play a crucial role in maintaining our platform's
          safety. If you encounter content or behavior that violates these
          guidelines:
        </p>
        <ul>
          <li>Use the report function available on all posts and profiles</li>
          <li>Provide detailed information about the violation</li>
          <li>Do not engage with or respond to violating content</li>
          <li>
            For urgent safety concerns, contact us immediately at
            safety@locuno.com
          </li>
        </ul>
        <p>
          All reports are reviewed promptly, and we take appropriate action
          based on our investigation findings.
        </p>
      </LegalSection>

      <LegalSection id="age-rating" title="Age Rating and Restrictions">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-800 font-medium">
            <strong>17+ Age Rating:</strong> Locuno is rated for users 17 years
            and older. This rating reflects the mature nature of community
            discussions and the responsibility required for local networking.
          </p>
        </div>
        <p>Users must be at least 17 years old to:</p>
        <ul>
          <li>Create a Locuno account</li>
          <li>Access platform features</li>
          <li>Participate in community discussions</li>
          <li>Use marketplace and local services</li>
        </ul>
        <p>
          Age verification may be required, and accounts found to belong to
          users under 17 will be immediately terminated.
        </p>
      </LegalSection>

      <LegalSection id="law-enforcement" title="Law Enforcement Cooperation">
        <p>
          <strong>Legal Compliance:</strong> Apple and Locuno may share
          offending material and user information with law enforcement agencies
          when required by law or when we believe it is necessary to:
        </p>
        <ul>
          <li>Comply with legal processes or government requests</li>
          <li>Protect the safety of our users or the public</li>
          <li>Investigate potential criminal activity</li>
          <li>Prevent harm to minors</li>
          <li>Address national security concerns</li>
        </ul>
        <p>
          We maintain detailed logs of user activity and content for the purpose
          of platform security and legal compliance. This information may be
          provided to authorities as required by applicable laws and
          regulations.
        </p>
      </LegalSection>

      <LegalSection id="updates" title="Updates to Guidelines">
        <p>
          These Community Guidelines may be updated periodically to address new
          challenges, legal requirements, or platform changes. Users will be
          notified of significant changes through:
        </p>
        <ul>
          <li>In-app notifications</li>
          <li>Email announcements</li>
          <li>Updates to this page with revision dates</li>
        </ul>
        <p>
          Continued use of Locuno after guideline updates constitutes acceptance
          of the revised terms.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact Information">
        <p>
          For questions about these Community Guidelines or to report serious
          violations, please contact us:
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mt-4">
          <p>
            <strong>Safety Team:</strong> safety@locuno.com
          </p>
          <p>
            <strong>Legal Team:</strong> legal@locuno.com
          </p>
          <p>
            <strong>General Support:</strong> support@locuno.com
          </p>
          <p>
            <strong>Address:</strong> Cloudjet Solutions Pte. Ltd.
            <br />
            71 Ayer Rajah Crescent
            <br />
            #02-10/11
            <br />
            Singapore 139951
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-blue-800">
            <strong>Emergency Situations:</strong> For immediate safety concerns
            or to report illegal activity, contact local law enforcement
            authorities in addition to notifying our safety team.
          </p>
        </div>
      </LegalSection>
    </LegalPageLayout>
  );
}
